/**
 * Incremental parser for `text/event-stream` bodies read with fetch (EventSource cannot send the
 * Authorization header). Handles chunk boundaries anywhere (even inside a line or a CRLF pair),
 * comment lines (`: ping`), multi-line `data:` fields and events without an `event:` name.
 */
export interface SseMessage {
  event: string;
  data: string;
}

export function createSseParser(onMessage: (msg: SseMessage) => void) {
  let buffer = '';
  let event = '';
  let data: string[] = [];

  const dispatch = () => {
    if (data.length) onMessage({ event: event || 'message', data: data.join('\n') });
    event = '';
    data = [];
  };

  const line = (raw: string) => {
    if (raw === '') return dispatch();
    if (raw.startsWith(':')) return; // comment / keep-alive
    const i = raw.indexOf(':');
    const field = i === -1 ? raw : raw.slice(0, i);
    let value = i === -1 ? '' : raw.slice(i + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
    // id / retry are not used by 1QA
  };

  return {
    feed(chunk: string) {
      buffer += chunk;
      for (;;) {
        const nl = buffer.search(/\r\n|\r|\n/);
        if (nl === -1) return;
        // A lone \r at the very end may be the first half of \r\n: wait for the next chunk.
        if (buffer[nl] === '\r' && nl === buffer.length - 1) return;
        const len = buffer[nl] === '\r' && buffer[nl + 1] === '\n' ? 2 : 1;
        const raw = buffer.slice(0, nl);
        buffer = buffer.slice(nl + len);
        line(raw);
      }
    },
    /** Call when the stream ends: dispatches a final event that had no trailing blank line. */
    end() {
      if (buffer) {
        line(buffer);
        buffer = '';
      }
      dispatch();
    },
  };
}

/** Reads a fetch body as SSE until it ends or `signal` aborts. */
export async function readSse(body: ReadableStream<Uint8Array>, onMessage: (msg: SseMessage) => void, signal?: AbortSignal) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser(onMessage);
  const onAbort = () => void reader.cancel().catch(() => {});
  signal?.addEventListener('abort', onAbort);
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    parser.feed(decoder.decode());
    parser.end();
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}
